<template>
  <template v-if="isMissingSettings">
    <DashboardMissingSettings />
  </template>
  <template v-else-if="isMissingFilter">
    <DashboardMissingFilter />
  </template>
  <template v-else-if="isMissingData">
    <DashboardMissingData />
  </template>
  <template v-else>
    <section class="heatmaps-section-header">
      <span class="heatmaps-header-text"> {{ t('dashboard__text__chart-heatmaps') }}</span>
    </section>
    <section class="chart-grid heatmaps">
      <template v-for="(hm, type) in heatmapMap" :key="type">
        <div
          v-if="hm.show"
          class="chart"
          :class="{ fullscreen: sharedData?.fullscreen, mobile: mobileScreen }"
        >
          <GraphHeatmapCard
            :key="`${sharedData?.fullscreen} ${isMissingData}`"
            :settings="heatmaps?.find((p) => p.type === type)"
            :accessKey="hm.accessKey"
            v-bind="hm.props"
          />
        </div>
      </template>
    </section>
  </template>
</template>
<script lang="ts" setup>
import DashboardMissingSettings from './DashboardMissingSettings.vue'
import DashboardMissingFilter from './DashboardMissingFilter.vue'
import DashboardMissingData from './DashboardMissingData.vue'
import GraphHeatmapCard from './cards/GraphHeatmapCard.vue'
// import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import { useHeatmapComponentMap } from '../composables/useHeatmapComponentMap'
import { computed, inject, toRefs, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRealm } from '@/composables/useRealm'
import { sharedDataKey } from '../../types'
import { useStore } from 'vuex'
import { FeatureType, useFilters } from '@/composables/useFilters'
import { useDataFetcher } from '@/composables/useDataFetcher'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
const sharedData = inject(sharedDataKey)
const realm = useRealm()
const filters = useFilters(FeatureType.Utilization)
const heatmap = useDataFetcher('fetch.utilization.heatmap')
const heatmaps = computed(() => realm.settings?.config?.utilization.heatmaps)
const { submitted } = toRefs(filters)
const heatmapMap = useHeatmapComponentMap(heatmap, submitted)
const store = useStore()
const mobileScreen = computed<boolean>(() => store.state.mobileScreen)
const { t } = useI18n()

type Props = {
  title: LocaleString
  info?: LocaleString
  heatmap?: any
  isMissingData: boolean
}
defineProps<Props>()

onMounted(() => {
  sharedData?.updateSectionIndex(2)
})

const isMissingSettings = computed(() => {
  if (!heatmaps.value && !realm.error) {
    return true
  }
  return false
})

const isMissingFilter = computed(() => {
  if (
    !filters.submitted?.complex &&
    filters.submitted?.buildings.length === 0 &&
    !filters.submitted?.level
  ) {
    return true
  }
  return false
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
.heatmaps-header-text {
  @include font-mixins.desktop-bold-paragraph;
  color: black;
}
.heatmaps-section-header {
  display: flex;
  padding: 2px;
  margin-top: 16px;
  margin-bottom: 16px;
  align-content: flex-start;
}
.chart-grid {
  flex-grow: 1;
  height: min-content;
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  gap: calc(variables.$gap-large * 2);
  row-gap: calc(variables.$gap-large * 2);
  margin-bottom: 16px;
  margin-top: 16px;
  &.heatmaps {
    display: flex;
    flex-direction: row;
    gap: calc(variables.$gap-large * 2);
    margin-top: 16px;
  }
}
.legend {
  position: fixed;
  top: 64px;
  right: 0;
  margin: 16px;
}
.chart {
  width: 100%;
  background-color: variables.$color-pure-white;
  border-radius: 8px;
  &.fullscreen:not(.mobile) {
    width: calc(50% - variables.$gap-large);
  }
}
</style>
